import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { SignOut, House, CurrencyDollar, Users, Plus, MapPin, ChatCircleDots, CaretRight, CaretDown, Package, Warning, Storefront, Trophy, UserCircle, Star, CalendarX, Crown, Presentation, Globe, ShieldWarning, Gauge, ChartBar, List, X, Clock } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import client from "../lib/api";
import { STATUS, STATUS_DOT, ROLE_LABEL, fmtDate, fmtShootTime, money, coverImage, onPropImgError, isGenericCover } from "../lib/helpers";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import PropertyDetail from "../components/PropertyDetail";
import DeliveryCountdown from "../components/DeliveryCountdown";
import PropertyDialog from "../components/PropertyDialog";
import BookingWidget from "./BookingWidget";
import AdminPanel from "./AdminPanel";
import PayPanel from "./PayPanel";
import { BrandMark } from "../components/BrandMark";
import MessagesPanel from "../components/MessagesPanel";
import AvailableShoots from "../components/AvailableShoots";
import BookingBlackouts from "../components/BookingBlackouts";
import PhotographerCalendar from "../components/PhotographerCalendar";
import CoverageDashboard from "../components/CoverageDashboard";
import CheckinOverrides from "../components/CheckinOverrides";
import OversightPanel from "../components/OversightPanel";
import CommandCenter from "../components/CommandCenter";
import ReportsPanel from "../components/ReportsPanel";
import ImpersonationBanner from "../components/ImpersonationBanner";
import BackupShoots from "../components/BackupShoots";
import PenthousePage from "../components/PenthousePage";
import LpgPanel from "../components/LpgPanel";
import LeadsPanel from "../components/LeadsPanel";
import LeadLanderHub from "../components/LeadLanderHub";
import ListAssist from "../components/ListAssist";
import PenthouseAdmin from "../components/PenthouseAdmin";
import BillingTable from "../components/BillingTable";
import DeliverablesPanel from "../components/DeliverablesPanel";
import DeliverablesKanban from "../components/DeliverablesKanban";
import ShootsMap from "../components/ShootsMap";
import ShootsCalendar from "../components/ShootsCalendar";
import ShootsList from "../components/ShootsList";
import MyProfile from "./MyProfile";
import ReferralTally from "../components/ReferralTally";
import RewardsPanel from "../components/RewardsPanel";
import LevelUpCelebration from "../components/LevelUpCelebration";
import HeaderRewardsChip from "../components/HeaderRewardsChip";
import NotificationBell from "../components/NotificationBell";
import ReferralPanel from "../components/ReferralPanel";
import { celebrate } from "../lib/celebrate";
import { toast } from "sonner";

function NavItem({ icon: Icon, label, active, onClick, testid, badge }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`group relative w-full flex items-center gap-3 pl-5 pr-3 py-2.5 rounded-md text-sm transition-[background-color,color] duration-200 ${active ? "bg-[#5C1A1A] text-white" : "text-white/60 hover:bg-white/[0.07] hover:text-white"}`}>
      <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full bg-[#B08D57] transition-all duration-300 ${active ? "h-6 opacity-100" : "h-0 opacity-0"}`} />
      <Icon size={20} weight={active ? "fill" : "duotone"} className={`transition-colors ${active ? "text-[#B08D57]" : "text-white/70 group-hover:text-white"}`} />
      <span className="flex-1 text-left">{label}</span>
      {badge > 0 && (
        <span data-testid={`${testid}-badge`}
          className="min-w-5 h-5 px-1.5 rounded-[5px] text-[11px] font-bold flex items-center justify-center bg-[#B08D57] text-[#4A0000]">
          {badge}
        </span>
      )}
    </button>
  );
}

function ShootCountdown({ when, testid }) {
  const target = when ? new Date(when).getTime() : null;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [target]);
  if (!target || isNaN(target)) return null;
  const diff = target - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const label = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  return (
    <span data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-[5px] bg-white/90 backdrop-blur-md border border-white/70 px-2.5 py-1 text-[11px] font-semibold text-[#2C2626] shadow-sm">
      <Clock size={12} weight="fill" className="text-[#B87333]" />
      in {label}
    </span>
  );
}


export default function Dashboard() {
  const { user, logout } = useAuth();
  const { impersonating } = useAuth();
  const [view, setView] = useState(user.role === "admin" ? "command" : "properties");
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailFocus, setDetailFocus] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileTab, setProfileTab] = useState("profile");
  const [leadLanderTarget, setLeadLanderTarget] = useState(null);
  const [partnerMenuOpen, setPartnerMenuOpen] = useState(false);
  // "Book a Shoot" / "New Shoot" open the booking wizard INSIDE the portal (no redirect,
  // no referral — the shoot is booked under the signed-in user's own account).
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => { setBookingOpen(true); setMenuOpen(false); };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("penthouse")) setView("penthouse");
  }, []);

  useEffect(() => {
    if (["lpg", "leadlanders", "listassist"].includes(view)) setPartnerMenuOpen(true);
  }, [view]);

  const isAdmin = user.role === "admin";
  const isEmployee = user.role === "employee";
  const isStaff = isAdmin || isEmployee;
  const isPilot = user.role === "pilot";
  const isCrew = user.role === "photographer" || isPilot || user.role === "dual";
  // Crew only ever see shoots assigned to them (backend already scopes this; guard client-side too).
  const gridProperties = isCrew
    ? properties.filter((p) => p.photographer_id === user.id || p.pilot_id === user.id)
    : properties;
  const partnerAllowed = isAdmin || (user.role === "agent" && user.penthouse_partner);
  const canCreate = user.role === "admin" || user.role === "employee" || user.role === "agent";
  const profileTabs = isCrew
    ? [["profile", "Profile"], ["bookingpage", "Booking Page"], ["grade", "My Grade"], ["rewards", "Rewards"], ["notifications", "Notifications"]]
    : user.role === "agent"
    ? [["profile", "Profile"], ["referral", "Refer & Earn"], ["rewards", "Rewards"], ["notifications", "Notifications"]]
    : isEmployee
    ? [["profile", "Profile"], ["rewards", "Rewards"], ["notifications", "Notifications"]]
    : [["profile", "Profile"], ["notifications", "Notifications"]];
  const openProfileTab = (val) => { setProfileTab(val); setView("profile"); };
  const HEAD = {
    properties: { eyebrow: "Operations", t: "Shoots" },
    command: { eyebrow: "Overview", t: "Command Center" },
    reports: { eyebrow: "Finance", t: "Reports" },
    pay: { eyebrow: "Finance", t: isAdmin ? "Payments" : "My Pay" },
    admin: { eyebrow: "Team", t: "Members" },
    messages: { eyebrow: "Communication", t: "Messages" },
    profile: { eyebrow: "Account", t: "My Profile" },
    bookingpage: { eyebrow: "Public Page", t: "My Booking Page" },
    billing: { eyebrow: "Finance", t: "Billing" },
    lpg: { eyebrow: "Marketing", t: "Presentation Generator" },
    leadlanders: { eyebrow: "Marketing", t: "LeadLander" },
    listassist: { eyebrow: "Marketing", t: "ListAssist" },
    penthouse_admin: { eyebrow: "Management", t: "Penthouse Partner" },
    deliverables: { eyebrow: "Delivery", t: "Deliverables" },
    blackouts: { eyebrow: "Operations", t: "Availability" },
    coverage: { eyebrow: "Operations", t: "Coverage" },
    oversight: { eyebrow: "Oversight", t: "Oversight" },
    rewards: { eyebrow: "Team", t: "Rewards" },
    pto: { eyebrow: "Schedule", t: "My Calendar" },
    penthouse: { eyebrow: "Upgrade", t: "Go Penthouse" },
    referral: { eyebrow: "Growth", t: "Refer & Earn" },
  };
  const [unread, setUnread] = useState(0);
  const [available, setAvailable] = useState([]);
  const [shootsView, setShootsView] = useState("map");
  const [backups, setBackups] = useState({ available: [], committed: [] });

  const refreshUnread = () => client.get("/messages/unread_count").then((r) => setUnread(r.data.count)).catch(() => {});
  const refreshAvailable = () => client.get("/properties/available").then((r) => setAvailable(r.data)).catch(() => {});
  const refreshBackups = () => {
    if (!isCrew) return;
    client.get("/properties/backups").then((r) => setBackups(r.data)).catch(() => {});
  };
  const [deliverablesReady, setDeliverablesReady] = useState(0);
  const refreshDeliverables = () => client.get("/deliverables").then((r) => setDeliverablesReady(r.data.ready_count)).catch(() => {});

  const load = async () => {
    const [p, u] = await Promise.all([
      client.get("/properties"),
      client.get("/users").catch(() => ({ data: [] })),
    ]);
    setProperties(p.data);
    setUsers(u.data);
  };
  useEffect(() => {
    load();
    refreshUnread();
    refreshAvailable();
    refreshDeliverables();
    refreshBackups();
    const iv = setInterval(() => { refreshUnread(); refreshAvailable(); refreshDeliverables(); refreshBackups(); }, 4000);
    return () => clearInterval(iv);
  }, []);

  const reloadAll = () => { load(); refreshAvailable(); refreshBackups(); refreshUnread(); };

  const addBackup = async (pid, slot) => {
    try {
      await client.post(`/properties/${pid}/backup${slot ? `?slot=${slot}` : ""}`);
      toast.success("You're on standby — added as a backup");
      reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add as backup");
    }
  };
  const withdrawBackup = async (pid, slot) => {
    try {
      await client.delete(`/properties/${pid}/backup${slot ? `?slot=${slot}` : ""}`);
      toast.success("Withdrawn as backup");
      reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not withdraw");
    }
  };

  const claimShoot = async (pid) => {
    try {
      await client.post(`/properties/${pid}/claim`);
      toast.success("Shoot claimed — it's now yours");
      load(); refreshAvailable(); refreshUnread();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim shoot");
    }
  };

  const openProperty = async (pid, focus = null) => {
    setDetailFocus(focus);
    setSelected(pid);
    try { await client.post(`/properties/${pid}/read`); } catch {}
    refreshUnread();
  };
  const closeProperty = async () => {
    const pid = selected;
    setSelected(null);
    setDetailFocus(null);
    if (pid) { try { await client.post(`/properties/${pid}/read`); } catch {} }
    refreshUnread();
  };

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("shoot");
    if (sid) {
      openProperty(sid);
      const url = new URL(window.location.href);
      url.searchParams.delete("shoot");
      window.history.replaceState({}, "", url.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? (u.name || u.email) : "Unassigned";
  };

  return (
    <div className={`min-h-screen flex bg-[#FAF9F6] ${impersonating ? "pt-10" : ""}`}>
      <ImpersonationBanner />
      {user.role === "agent" && <LevelUpCelebration />}
      {/* Mobile drawer backdrop */}
      {menuOpen && (
        <div data-testid="sidebar-backdrop" onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-30 bg-[#2A0A0A]/50 backdrop-blur-sm lg:hidden" />
      )}
      {/* Floating iPadOS-style sidebar — off-canvas drawer on mobile */}
      <aside className={`w-[82vw] max-w-[280px] lg:w-60 fixed ${impersonating ? "top-[52px]" : "top-3"} bottom-3 left-3 z-40 rounded-[5px] bg-gradient-to-b from-[#4A0000] to-[#2A0A0A] flex flex-col overflow-hidden shadow-[0_28px_80px_-24px_rgba(74,0,0,0.6)] ring-1 ring-white/[0.06] transition-transform duration-300 ${menuOpen ? "translate-x-0" : "-translate-x-[110%]"} lg:translate-x-0`}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10">
          <span className="shrink-0"><BrandMark logoSize={28} textClass="text-white" textSize="text-xl" /></span>
          {user.role === "agent" && user.penthouse_partner && (
            <span data-testid="penthouse-partner-pill"
              className="shrink inline-flex items-center gap-1 rounded-[5px] bg-gradient-to-r from-[#C9A25A] to-[#B08D57] text-[#1A1715] text-[8px] font-black uppercase tracking-[0.1em] px-1.5 py-0.5 whitespace-nowrap overflow-hidden shadow-[0_4px_14px_-4px_rgba(176,141,87,0.7)] ring-1 ring-[#E9C77E]/50">
              <Crown size={10} weight="fill" className="shrink-0" /><span className="truncate">Penthouse Partner</span>
            </span>
          )}
          <button data-testid="sidebar-close" onClick={() => setMenuOpen(false)}
            className="ml-auto shrink-0 lg:hidden text-white/60 hover:text-white p-1"><X size={20} weight="bold" /></button>
        </div>
        <nav onClick={() => setMenuOpen(false)} className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto reelty-scroll">
          <p className="eyebrow text-white/35 px-5 pt-3 pb-2">Workspace</p>
          {isAdmin && (
            <NavItem testid="nav-command" icon={Gauge} label="Command Center" active={view === "command"} onClick={() => setView("command")} />
          )}
          <NavItem testid="nav-properties" icon={House} label="Shoots" active={view === "properties"} onClick={() => setView("properties")} badge={available.length} />
          <NavItem testid="nav-messages" icon={ChatCircleDots} label="Messages" active={view === "messages"} onClick={() => setView("messages")} badge={unread} />
          <NavItem testid="nav-deliverables" icon={Package} label="Deliverables" active={view === "deliverables"} onClick={() => setView("deliverables")} badge={user.role === "agent" ? deliverablesReady : 0} />
          {user.role === "agent" && (
            <NavItem testid="nav-billing" icon={CurrencyDollar} label="Billing" active={view === "billing"} onClick={() => setView("billing")} />
          )}
          {(isAdmin || (user.role === "agent" && user.penthouse_partner)) && (
            <div>
              <button data-testid="nav-partner-suite"
                onClick={(e) => { e.stopPropagation(); setPartnerMenuOpen((o) => !o); }}
                className={`group relative w-full flex items-center gap-3 pl-5 pr-3 py-2.5 rounded-md text-sm transition-[background-color,color] duration-200 ${["lpg", "leadlanders", "listassist", "penthouse_admin", "demo_leads"].includes(view) ? "bg-[#5C1A1A] text-white" : "text-white/60 hover:bg-white/[0.07] hover:text-white"}`}>
                <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full bg-[#B08D57] transition-all duration-300 ${["lpg", "leadlanders", "listassist", "penthouse_admin", "demo_leads"].includes(view) ? "h-6 opacity-100" : "h-0 opacity-0"}`} />
                <Crown size={20} weight={["lpg", "leadlanders", "listassist", "penthouse_admin", "demo_leads"].includes(view) ? "fill" : "duotone"} className={`transition-colors ${["lpg", "leadlanders", "listassist", "penthouse_admin", "demo_leads"].includes(view) ? "text-[#B08D57]" : "text-white/70 group-hover:text-white"}`} />
                <span className="flex-1 text-left">Penthouse Partner</span>
                <CaretDown size={14} className={`text-white/40 transition-transform duration-200 ${partnerMenuOpen ? "rotate-180" : ""}`} />
              </button>
              {partnerMenuOpen && (
                <div className="mt-1 ml-6 pl-3 border-l border-white/10 space-y-0.5" data-testid="partner-submenu">
                  {(isAdmin ? [["penthouse_admin", "Management"], ["lpg", "Presentation Generator"], ["leadlanders", "LeadLander"], ["listassist", "ListAssist"], ["demo_leads", "Demo Leads"]] : [["lpg", "Presentation Generator"], ["leadlanders", "LeadLander"], ["listassist", "ListAssist"]]).map(([val, label]) => (
                    <button key={val} data-testid={`nav-${val}`}
                      onClick={() => { if (val === "leadlanders") setLeadLanderTarget(null); setView(val); }}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${view === val ? "text-[#B08D57] font-medium bg-white/[0.05]" : "text-white/55 hover:text-white hover:bg-white/[0.05]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {isCrew && (
            <NavItem testid="nav-my-pay" icon={CurrencyDollar} label="My Pay" active={view === "pay"} onClick={() => setView("pay")} />
          )}
          {isCrew && (
            <NavItem testid="nav-my-pto" icon={CalendarX} label="My Calendar" active={view === "pto"} onClick={() => setView("pto")} />
          )}
          {!isAdmin && (
          <div>
            <button data-testid="nav-my-profile"
              onClick={(e) => { e.stopPropagation(); setView("profile"); setProfileOpen((o) => !o); }}
              className={`group relative w-full flex items-center gap-3 pl-5 pr-3 py-2.5 rounded-md text-sm transition-[background-color,color] duration-200 ${view === "profile" ? "bg-[#5C1A1A] text-white" : "text-white/60 hover:bg-white/[0.07] hover:text-white"}`}>
              <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full bg-[#B08D57] transition-all duration-300 ${view === "profile" ? "h-6 opacity-100" : "h-0 opacity-0"}`} />
              <UserCircle size={20} weight={view === "profile" ? "fill" : "duotone"} className={`transition-colors ${view === "profile" ? "text-[#B08D57]" : "text-white/70 group-hover:text-white"}`} />
              <span className="flex-1 text-left">My Profile</span>
              {profileTabs.length > 1 && <CaretDown size={14} className={`text-white/40 transition-transform duration-200 ${profileOpen ? "rotate-180" : ""}`} />}
            </button>
            {profileOpen && profileTabs.length > 1 && (
              <div className="mt-1 ml-6 pl-3 border-l border-white/10 space-y-0.5" data-testid="profile-submenu">
                {profileTabs.map(([val, label]) => (
                  <button key={val} data-testid={`nav-profile-${val}`}
                    onClick={() => openProfileTab(val)}
                    className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${view === "profile" && profileTab === val ? "text-[#B08D57] font-medium bg-white/[0.05]" : "text-white/55 hover:text-white hover:bg-white/[0.05]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
          {isAdmin && (
            <NavItem testid="nav-pay" icon={CurrencyDollar} label="Payments" active={view === "pay"} onClick={() => setView("pay")} />
          )}
          {isAdmin && (
            <NavItem testid="nav-reports" icon={ChartBar} label="Reports" active={view === "reports"} onClick={() => setView("reports")} />
          )}
          {isStaff && (
            <NavItem testid="nav-admin" icon={Users} label="Members" active={view === "admin"} onClick={() => setView("admin")} />
          )}
          {isAdmin && (
            <NavItem testid="nav-blackouts" icon={CalendarX} label="Availability" active={view === "blackouts"} onClick={() => setView("blackouts")} />
          )}
          {isStaff && (
            <NavItem testid="nav-coverage" icon={MapPin} label="Coverage" active={view === "coverage"} onClick={() => setView("coverage")} />
          )}
          {isStaff && (
            <NavItem testid="nav-oversight" icon={ShieldWarning} label="Oversight" active={view === "oversight"} onClick={() => setView("oversight")} />
          )}
          {isAdmin && (
            <>
              <NavItem testid="nav-rewards" icon={Trophy} label="Rewards" active={view === "rewards"} onClick={() => setView("rewards")} />
            </>
          )}
          {user.role === "agent" && !user.penthouse_partner && (
            <motion.button
              data-testid="penthouse-nav-badge" onClick={() => setView("penthouse")}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              animate={{ boxShadow: ["0 0 0 0 rgba(176,141,87,0.55)", "0 0 0 10px rgba(176,141,87,0)"] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="relative mt-3 w-full overflow-hidden flex items-center gap-2.5 rounded-xl px-3 py-3 bg-gradient-to-br from-[#C6A253] via-[#B08D57] to-[#8A6D2E] text-[#1A1715] text-left group">
              <motion.span aria-hidden
                animate={{ x: ["-130%", "230%"] }} transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.6, ease: "easeInOut" }}
                className="absolute inset-y-0 w-1/3 bg-white/40 blur-md -skew-x-12 pointer-events-none" />
              <span className="relative shrink-0 grid place-items-center w-8 h-8 rounded-full bg-[#4A0000]/15">
                <Crown size={18} weight="fill" className="text-[#4A0000] transition-transform group-hover:scale-110 group-hover:-rotate-6" />
              </span>
              <span className="relative flex-1 min-w-0">
                <span className="flex items-center gap-1.5">
                  <span className="text-sm font-extrabold leading-none tracking-tight">Go Penthouse</span>
                  <span className="text-[9px] font-black uppercase tracking-wider bg-[#4A0000] text-[#E9C77E] px-1.5 py-0.5 rounded-[5px]">Free</span>
                </span>
                <span className="block text-[11px] font-semibold opacity-80 mt-1">Claim your free upgrade →</span>
              </span>
              <CaretRight size={15} weight="bold" className="relative shrink-0 transition-transform group-hover:translate-x-0.5" />
            </motion.button>
          )}
          {(user.role === "agent" || isStaff) && (
            <button onClick={openBooking} data-testid="nav-book-shoot"
              className="mt-10 w-full flex items-center justify-center gap-2 rounded-md px-3 py-2.5 bg-gradient-to-r from-[#C9A25A] to-[#B08D57] text-[#1A1715] text-sm font-bold shadow-[0_8px_22px_-8px_rgba(176,141,87,0.8)] ring-1 ring-[#E9C77E]/50 hover:brightness-[1.05] hover:-translate-y-0.5 transition-all">
              <Plus size={16} weight="bold" /> Book a Shoot
            </button>
          )}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <button data-testid="profile-button" onClick={() => { setView("profile"); setMenuOpen(false); }}
            className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors border ${view === "profile" ? "bg-white/[0.08] border-white/15" : "border-transparent hover:bg-white/[0.06]"}`}>
            <div className="w-9 h-9 rounded-full bg-[#FBFAF7] flex items-center justify-center text-[#4A0000] font-bold overflow-hidden ring-2 ring-white/10">
              {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : (user.name || user.email)[0].toUpperCase()}
            </div>
            <div className="min-w-0 text-left flex-1">
              <p className="text-white text-sm truncate font-medium">{user.name || user.email}</p>
              <p className="text-[#B08D57]/80 text-xs truncate whitespace-nowrap">{ROLE_LABEL[user.role]} · View profile</p>
            </div>
            <CaretRight size={16} className="text-white/40" />
          </button>
          <button data-testid="logout-button" onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-white/55 hover:bg-white/[0.07] hover:text-white transition-colors">
            <SignOut size={20} weight="duotone" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-[272px] min-w-0">
        <header className={`sticky ${impersonating ? "top-10" : "top-0"} z-20 bg-[#4A0000] border-b border-[#B08D57]/35 px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between gap-3`}>
          <button data-testid="menu-toggle" onClick={() => setMenuOpen(true)}
            className="lg:hidden shrink-0 -ml-1 p-2 rounded-md text-[#FBFAF7] hover:bg-white/10 transition-colors"><List size={24} weight="bold" /></button>
          <div className="min-w-0 flex-1 overflow-hidden">
            <p className="eyebrow text-[#D8B978] truncate text-[10px] sm:text-xs leading-none">{HEAD[view]?.eyebrow || "Reelty Portal"}</p>
            <h1 className="font-display text-xl sm:text-2xl lg:text-3xl tracking-tight leading-tight py-0.5 mt-0 text-[#FBFAF7] truncate">{HEAD[view]?.t || "Reelty"}</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {user.role === "agent" && <div className="hidden sm:block"><HeaderRewardsChip onRedeem={() => openProfileTab("rewards")} /></div>}
            <NotificationBell onNavigate={(n) => {
              const focusFor = { message: "messages", deliverables: "deliverables", review: "review" };
              if (n.pid) { setView("properties"); openProperty(n.pid, focusFor[n.kind] || null); return; }
              if (n.kind === "payout") { setView("pay"); return; }
              if (n.kind === "message") { setView("messages"); return; }
              setView("properties");
            }} />
            {view === "properties" && canCreate && (
              <Button data-testid="new-shoot-button" onClick={openBooking}
                className="bg-[#B08D57] hover:bg-[#C59B47] text-[#1A1715] font-semibold px-3 sm:px-5 rounded-[5px] transition-transform hover:-translate-y-0.5 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] whitespace-nowrap">
                <Plus size={18} className="sm:mr-1" weight="bold" /> <span className="hidden sm:inline">New Shoot</span>
              </Button>
            )}
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8">
          {view === "properties" && (
            <>
            {(user.role === "photographer" || user.role === "dual") && <ReferralTally />}
            {isCrew && !user.base_zip && (
              <div data-testid="base-zip-nudge" className="mb-6 flex items-center gap-3 border border-[#B08D57]/40 bg-[#B08D57]/10 rounded-lg px-4 py-3">
                <MapPin size={20} weight="fill" className="text-[#B08D57] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#4A0000]">Set your base ZIP</p>
                  <p className="text-xs text-[#8A6D1B]">Add your home base so you get matched to nearby shoots and appear on the coverage map.</p>
                </div>
                <Button data-testid="base-zip-nudge-btn" size="sm" onClick={() => setView("profile")} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white shrink-0">Add base ZIP</Button>
              </div>
            )}
            {isCrew
              ? <BackupShoots openShoots={available} role={user.role} tier={user.tier} onClaim={claimShoot}
                  available={backups.available} committed={backups.committed}
                  onAddBackup={addBackup} onWithdraw={withdrawBackup} onOpen={openProperty} />
              : <AvailableShoots items={available} role={user.role} tier={user.tier} onClaim={claimShoot} onOpen={openProperty} />}
            {isStaff && properties.length > 0 && (
              <div className="mb-2">
                <div className="flex rounded-md border border-[#E9DDC6] overflow-hidden text-sm w-fit mb-4" data-testid="shoots-view-toggle">
                  {[["map", "Map"], ["calendar", "Calendar"], ["list", "List"]].map(([v, lbl]) => (
                    <button key={v} data-testid={`shoots-view-${v}`} onClick={() => setShootsView(v)}
                      className={`px-4 py-2 transition-colors ${shootsView === v ? "bg-[#4A0000] text-white font-semibold" : "bg-white text-[#5C5A58] hover:bg-[#FAF9F6]"}`}>{lbl}</button>
                  ))}
                </div>
                {shootsView === "map"
                  ? <ShootsMap properties={properties} onOpen={openProperty} />
                  : shootsView === "calendar"
                  ? <ShootsCalendar properties={properties} onOpen={openProperty} />
                  : <ShootsList properties={properties} users={users} onOpen={openProperty} />}
              </div>
            )}
            {isCrew && properties.length > 0 && (
              <ShootsMap variant="photographer" properties={properties} onOpen={openProperty} />
            )}
            {gridProperties.length === 0 ? (
              <div className="text-center py-24 text-[#5C5A58]">
                <House size={48} weight="duotone" className="mx-auto mb-4 text-[#4A0000]" />
                <p>No shoots yet.{canCreate ? " Create your first one." : ""}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="property-grid">
                {gridProperties.map((p, i) => {
                  const st = STATUS[p.status] || STATUS.scheduled;
                  return (
                    <motion.button key={p.id} data-testid={`property-card-${p.id}`}
                      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                      onClick={() => openProperty(p.id)}
                      className="card-shoot text-left group">
                      <div className="h-52 sm:h-56 bg-[#2A0A0A] overflow-hidden relative">
                        <img src={coverImage(p)} alt={p.title} onError={onPropImgError} className="block w-full h-full object-cover object-top transition-transform duration-[700ms] ease-out group-hover:scale-[1.06]" />
                        {isGenericCover(p) && (
                          <div data-testid={`generic-image-overlay-${p.id}`}
                            className="absolute inset-0 bg-[#4A0000]/45 mix-blend-multiply pointer-events-none" />
                        )}
                        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(42,10,10,0.93) 4%, rgba(42,10,10,0.55) 36%, rgba(42,10,10,0.04) 70%)" }} />
                        <span data-testid={`shoot-status-badge-${p.id}`}
                          className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-[5px] bg-white/90 backdrop-blur-md border border-white/70 px-2.5 py-1 text-[11px] font-semibold text-[#2C2626] shadow-sm">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_DOT[p.status] || "#4A0000" }} />
                          {st.label}
                        </span>
                        <div className="absolute top-3 right-3 flex items-center gap-2">
                          {p.status === "scheduled" && <ShootCountdown when={p.shoot_date} testid={`shoot-countdown-${p.id}`} />}
                          {p.weather_alert && (
                            <Badge data-testid={`weather-alert-badge-${p.id}`}
                              className={`border shadow-sm flex items-center gap-1 ${p.weather_alert.level === "severe" ? "bg-red-600 text-white border-red-700" : "bg-amber-100 text-amber-900 border-amber-300"}`}>
                              <Warning size={12} weight="fill" /> {p.weather_alert.level === "severe" ? "Severe weather" : "Weather watch"}
                            </Badge>
                          )}
                        </div>
                        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
                          <h3 className="font-display text-[1.35rem] sm:text-2xl leading-tight text-[#FBFAF7] truncate drop-shadow-[0_1px_10px_rgba(0,0,0,0.45)]">{p.title}</h3>
                          <p className="text-xs sm:text-sm text-[#F1EDE4]/85 flex items-center gap-1 mt-1 min-w-0"><MapPin size={13} weight="duotone" className="shrink-0 text-[#E9C77E]" /><span className="truncate">{p.address}</span></p>
                        </div>
                      </div>
                      <div className="p-5">
                        {p.package_type && (
                          <div className="flex items-center gap-1.5 mb-4" data-testid={`package-badge-${p.id}`}>
                            <Badge className="bg-[#4A0000]/5 text-[#4A0000] border-[#4A0000]/20 flex items-center gap-1"><Package size={11} weight="fill" />{p.package_type}</Badge>
                            {p.drone && <Badge data-testid={`drone-badge-${p.id}`} className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40">Drone</Badge>}
                          </div>
                        )}
                        {p.booked_via_slug && user.role !== "admin" && (
                          (user.role === "photographer" || user.role === "dual") && p.commission_photographer_id === user.id
                            ? <div className="mb-4" data-testid={`booked-via-you-${p.id}`}>
                                <Badge className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40 flex items-center gap-1 w-fit"><Storefront size={11} weight="fill" />Booked via your page · +10%</Badge>
                              </div>
                            : <div className="mb-4" data-testid={`booked-via-${p.id}`}>
                                <Badge className="bg-[#4A0000]/5 text-[#4A0000] border-[#4A0000]/20 flex items-center gap-1 w-fit"><Storefront size={11} weight="fill" />Booked via {p.booked_via_name || "a photographer"}'s page</Badge>
                              </div>
                        )}
                        <div className="flex items-start justify-between text-sm">
                          <div className="text-[#5C5A58]">
                            <div>{fmtDate(p.shoot_date)}</div>
                            {fmtShootTime(p.shoot_date) && <div>{fmtShootTime(p.shoot_date)}</div>}
                          </div>
                          {isCrew && (
                            <span data-testid={`card-payout-${p.id}`} className="font-display text-[#4A0000] font-semibold">{money(
                              p.photographer_id === user.id
                                ? (p.flat_fee || 0) + (p.bonus || 0) + (p.edited ? p.edit_fee || 0 : 0) + (p.commission_photographer_id === user.id ? (p.commission || 0) : 0)
                                : p.pilot_id === user.id
                                ? (p.pilot_flat_fee || 0) + (p.pilot_bonus || 0)
                                : 0
                            )}</span>
                          )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-[#FAF9F6] text-xs text-[#5C5A58] grid grid-cols-2 gap-3">
                          <div>
                            <p className="uppercase tracking-wider text-[10px] text-[#9A968E]">Agent</p>
                            <p className="text-[#1A1715] font-medium truncate" data-testid={`card-agent-${p.id}`}>{userName(p.agent_id)}</p>
                          </div>
                          <div>
                            <p className="uppercase tracking-wider text-[10px] text-[#9A968E]">Photographer</p>
                            <p className="text-[#1A1715] font-medium truncate" data-testid={`card-photographer-${p.id}`}>{p.needs_photographer === false ? "—" : userName(p.photographer_id)}</p>
                          </div>
                        </div>
                        {(p.needs_pilot || p.pilot_id) && (
                          <div className="mt-2 text-xs text-[#5C5A58]">
                            <p className="uppercase tracking-wider text-[10px] text-[#9A968E]">Drone pilot</p>
                            <p className="text-[#1A1715] font-medium truncate" data-testid={`card-pilot-${p.id}`}>{userName(p.pilot_id)}</p>
                          </div>
                        )}
                        {isCrew && (p.photographer_id === user.id || p.pilot_id === user.id) && p.crew_upload_url && (
                          <a href={p.crew_upload_url} target="_blank" rel="noreferrer" data-testid={`card-crew-upload-${p.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-3 flex items-center justify-center gap-2 w-full bg-[#4A0000] text-white rounded-md py-2 text-xs font-semibold hover:bg-[#5C1A1A] transition-colors">
                            <Package size={14} weight="fill" /> Upload your files here
                          </a>
                        )}
                        {isCrew && (p.photographer_id === user.id || p.pilot_id === user.id) && !p.deliverables_ready && p.status !== "cancelled" && (
                          <div className="mt-3" data-testid={`card-countdown-${p.id}`}><DeliveryCountdown p={p} compact /></div>
                        )}
                        {user.role === "agent" && p.agent_id === user.id && p.status === "completed" && p.deliverables_ready && !p.review_rating && (
                          <div data-testid={`card-review-nudge-${p.id}`} className="mt-3 flex items-center justify-center gap-1.5 w-full bg-[#B08D57]/12 text-[#8A6D1B] border border-[#B08D57]/40 rounded-md py-2 text-xs font-semibold">
                            <Star size={13} weight="fill" /> Rate your photographer
                          </div>
                        )}
                        {p.deliverables_ready && p.deliverables_url && (
                          user.role === "agent" && !p.client_paid
                            ? <div role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setView("billing"); }} data-testid={`card-deliverables-locked-${p.id}`}
                                className="mt-3 flex items-center justify-center gap-1.5 w-full overflow-hidden whitespace-nowrap bg-[#4A0000] text-white rounded-md px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.06em] leading-none shadow-[0_12px_28px_-8px_rgba(74,0,0,0.65)] ring-1 ring-[#B08D57]/40 hover:bg-[#5C1A1A] hover:shadow-[0_16px_34px_-8px_rgba(74,0,0,0.7)] transition-all cursor-pointer">
                                <CurrencyDollar size={13} weight="fill" className="text-[#B08D57] shrink-0" /> <span className="truncate">Pay invoice to unlock deliverables</span>
                              </div>
                            : <a href={p.deliverables_url} target="_blank" rel="noreferrer" data-testid={`card-deliverables-${p.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="mt-3 flex items-center justify-center gap-1.5 w-full overflow-hidden whitespace-nowrap bg-[#B08D57] text-[#2A0A0A] rounded-md px-3 py-2.5 text-xs font-semibold leading-none hover:bg-[#9A7B3F] hover:text-[#FBFAF7] transition-colors">
                                <Package size={14} weight="fill" className="shrink-0" /> <span className="truncate">Deliverables ready — open</span>
                              </a>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
            </>
          )}
          {view === "pay" && <PayPanel />}
          {view === "command" && <CommandCenter onOpenProperty={(pid) => openProperty(pid)} onGo={(v) => setView(v)} />}
          {view === "reports" && <ReportsPanel />}
          {view === "rewards" && <RewardsPanel />}
          {view === "referral" && <ReferralPanel />}
          {view === "admin" && <AdminPanel users={users} reload={load} />}
          {view === "blackouts" && <BookingBlackouts />}
          {view === "coverage" && <CoverageDashboard onOpen={openProperty} />}
          {view === "oversight" && <OversightPanel />}
          {view === "penthouse" && <PenthousePage />}
          {view === "penthouse_admin" && isAdmin && (
            <PenthouseAdmin onOpenLeadLander={(id) => { setLeadLanderTarget(id); setView("leadlanders"); }} />
          )}
          {view === "lpg" && (partnerAllowed ? <LpgPanel /> : <PenthousePage />)}
          {view === "leadlanders" && (partnerAllowed ? <LeadLanderHub initialPropertyId={leadLanderTarget} onConsumed={() => setLeadLanderTarget(null)} onGoPenthouse={() => setView("penthouse")} /> : <PenthousePage />)}
          {view === "listassist" && (partnerAllowed ? <ListAssist /> : <PenthousePage />)}
          {view === "demo_leads" && isAdmin && <LeadsPanel />}
          {view === "pto" && <PhotographerCalendar />}
          {view === "messages" && <MessagesPanel onOpen={openProperty} />}
          {view === "billing" && <BillingTable />}
          {view === "deliverables" && (isStaff
            ? <DeliverablesKanban onOpenProperty={openProperty} />
            : <DeliverablesPanel role={user.role} onOpenProperty={openProperty} />)}
          {view === "profile" && <MyProfile tab={profileTab} onTabChange={setProfileTab} />}
        </div>
      </main>

      <PropertyDetail propertyId={selected} open={!!selected} onClose={closeProperty} focus={detailFocus}
        users={users} onChanged={reloadAll} onEdit={(p) => { setSelected(null); setEditing(p); setDialogOpen(true); }}
        onManageLeadLander={(pid) => { setSelected(null); setLeadLanderTarget(pid); setView("leadlanders"); }}
        onGoPenthouse={() => { setSelected(null); setView("penthouse"); }} />
      <PropertyDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} users={users} onSaved={load} />
      {bookingOpen && (
        <div className="fixed inset-0 z-[100] bg-[#FBFAF7] overflow-y-auto" data-testid="portal-booking-overlay">
          <BookingWidget embedded currentUser={user} onClose={() => { setBookingOpen(false); reloadAll?.(); }} />
        </div>
      )}
    </div>
  );
}
